import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { TrendUp, TrendDown, Sliders, ArrowsClockwise, DownloadSimple, FileCsv, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import useClickOutside from "../lib/useClickOutside";
import { useAuth } from "../context/AuthContext";

const txnColor = {
  in: "bg-emerald-600 text-white",
  out: "bg-red-600 text-white",
  adjustment: "bg-amber-400 text-amber-950",
};
const txnIcon = { in: TrendUp, out: TrendDown, adjustment: Sliders };

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);
  useClickOutside(exportRef, () => setExportOpen(false), exportOpen);
  const { hasRole } = useAuth();
  const showCost = !hasRole("viewer");

  const downloadExport = async (format) => {
    setExportOpen(false);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ format });
      if (type) params.set("type", type);
      const res = await fetch(`${API}/exports/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const load = async () => {
    setLoading(true);
    const params = { limit: 500 };
    if (type) params.type = type;
    const { data } = await api.get("/transactions", { params });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [type]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">
            Audit · Movement log
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mt-1">
            Transactions
          </h1>
        </div>
        <div className="relative" ref={exportRef}>
          <button
            onClick={() => setExportOpen((v) => !v)}
            data-testid="txn-export-button"
            className="inline-flex items-center gap-1.5 sm:gap-2 border border-zinc-300 bg-white hover:bg-zinc-100 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold"
          >
            <DownloadSimple size={16} weight="bold" />
            Export
          </button>
          {exportOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-zinc-300 shadow-lg z-30 w-44">
              <button
                onClick={() => downloadExport("csv")}
                data-testid="export-txn-csv"
                className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-100 text-sm"
              >
                <FileCsv size={16} weight="duotone" />
                CSV spreadsheet
              </button>
              <button
                onClick={() => downloadExport("pdf")}
                data-testid="export-txn-pdf"
                className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-100 text-sm border-t border-zinc-200"
              >
                <FilePdf size={16} weight="duotone" />
                PDF report
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-px bg-zinc-300 w-full sm:w-fit overflow-x-auto">
        {[
          { v: "", label: "All" },
          { v: "in", label: "IN" },
          { v: "out", label: "OUT" },
          { v: "adjustment", label: "Adj" },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => setType(o.v)}
            data-testid={`txn-filter-${o.v || "all"}`}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-[10px] sm:text-xs font-bold uppercase tracking-widest whitespace-nowrap ${
              type === o.v ? "bg-[#1A2332] text-white" : "bg-white hover:bg-zinc-100"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-2">
        {loading && (
          <div className="bg-white border border-zinc-300 p-6 text-center text-xs uppercase tracking-widest text-zinc-500 font-mono">
            Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="bg-white border border-zinc-300 p-8 text-center">
            <ArrowsClockwise size={28} weight="duotone" className="mx-auto text-zinc-300" />
            <div className="text-sm text-zinc-500 mt-2">No transactions yet.</div>
          </div>
        )}
        {items.map((t) => {
          const Icon = txnIcon[t.type];
          return (
            <div key={t.id} data-testid={`txn-card-${t.id}`} className="bg-white border border-zinc-300 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${txnColor[t.type]}`}>
                      <Icon size={9} weight="bold" /> {t.type}
                    </span>
                    <span className="capitalize text-[10px] text-zinc-600">{t.reason}</span>
                  </div>
                  <div className="font-medium text-sm mt-1 truncate">{t.part_name}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{t.part_sku}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-mono font-bold text-base">
                    {t.type === "out" ? "−" : t.type === "in" ? "+" : "="}{t.quantity}
                  </div>
                  {showCost && (
                    <div className={`font-mono text-xs font-bold ${
                      t.type === "in" ? "text-emerald-700" : t.type === "out" ? "text-red-700" : "text-zinc-700"
                    }`}>
                      ${Number(t.total_cost || 0).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-100 text-[10px] text-zinc-500 font-mono">
                <span>
                  {new Date(t.created_at).toLocaleString("en-US", {
                    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </span>
                <span className="truncate ml-2">{t.user_name}</span>
              </div>
              {t.note && (
                <div className="mt-1.5 text-xs text-zinc-600 italic">{t.note}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="hidden md:block bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">When</th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Type</th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Part</th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Reason</th>
              <th className="text-right px-4 py-3 border-b-2 border-zinc-900">Qty</th>
              {showCost && <th className="text-right px-4 py-3 border-b-2 border-zinc-900">Unit cost</th>}
              {showCost && <th className="text-right px-4 py-3 border-b-2 border-zinc-900">Total</th>}
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Operator</th>
              <th className="text-left px-4 py-3 border-b-2 border-zinc-900">Note</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-xs uppercase tracking-widest text-zinc-500 font-mono">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-16 text-center">
                  <ArrowsClockwise size={28} weight="duotone" className="mx-auto text-zinc-300" />
                  <div className="text-sm text-zinc-500 mt-2">No transactions yet.</div>
                </td>
              </tr>
            )}
            {items.map((t) => {
              const Icon = txnIcon[t.type];
              return (
                <tr key={t.id} className="row-hover" data-testid={`txn-row-${t.id}`}>
                  <td className="px-4 py-3 border-b border-zinc-200 font-mono text-xs text-zinc-600 whitespace-nowrap">
                    {new Date(t.created_at).toLocaleString("en-US", {
                      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
                    })}
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${txnColor[t.type]}`}>
                      <Icon size={10} weight="bold" /> {t.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200">
                    <div className="font-medium">{t.part_name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{t.part_sku}</div>
                  </td>
                  <td className="px-4 py-3 border-b border-zinc-200 capitalize text-zinc-700">{t.reason}</td>
                  <td className="px-4 py-3 border-b border-zinc-200 text-right font-mono font-bold">
                    {t.type === "out" ? "−" : t.type === "in" ? "+" : "="}{t.quantity}
                  </td>
                  {showCost && (
                    <td className="px-4 py-3 border-b border-zinc-200 text-right font-mono text-xs text-zinc-600">
                      ${Number(t.unit_cost || 0).toFixed(2)}
                    </td>
                  )}
                  {showCost && (
                    <td className={`px-4 py-3 border-b border-zinc-200 text-right font-mono text-xs font-bold ${
                      t.type === "in" ? "text-emerald-700" : t.type === "out" ? "text-red-700" : "text-zinc-700"
                    }`}>
                      ${Number(t.total_cost || 0).toFixed(2)}
                    </td>
                  )}
                  <td className="px-4 py-3 border-b border-zinc-200 text-zinc-700">{t.user_name}</td>
                  <td className="px-4 py-3 border-b border-zinc-200 text-zinc-500 text-xs max-w-[280px] truncate">
                    {t.note || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
